import { CheckCircle2, Package, Clock, Store, Truck, PartyPopper, XCircle } from "lucide-react";
import { STORE_ADDRESS } from "@/constants/store";

const PICKUP_STEPS = [
  { key: "paid", label: "Order Placed", desc: "Payment confirmed", Icon: CheckCircle2 },
  { key: "processing", label: "Preparing", desc: "We're packing your items", Icon: Package },
  { key: "ready_for_pickup", label: "Ready for Pickup", desc: `Waiting at ${STORE_ADDRESS.short}`, Icon: Store },
  { key: "completed", label: "Picked Up", desc: "Order complete", Icon: PartyPopper },
];

const DELIVERY_STEPS = [
  { key: "paid", label: "Order Placed", desc: "Payment confirmed", Icon: CheckCircle2 },
  { key: "processing", label: "Preparing", desc: "We're packing your items", Icon: Package },
  { key: "out_for_delivery", label: "Out for Delivery", desc: "Driver is on the way", Icon: Truck },
  { key: "completed", label: "Delivered", desc: "Order complete", Icon: PartyPopper },
];

export const OrderStatusTimeline = ({ order }) => {
  const special = !!order.has_special_order;
  const eta = order.estimated_ready_days;
  let steps = order.fulfillment === "pickup" ? PICKUP_STEPS : DELIVERY_STEPS;
  if (special) {
    steps = steps.map((s) =>
      s.key === "processing"
        ? { ...s, label: "Ordering your item", desc: `We're bringing your item in${eta ? ` — ETA ~${eta} days` : ""}` }
        : s
    );
  }
  const history = order.status_history || [];
  const timeFor = (key) => {
    const h = history.filter((e) => e.status === key).pop();
    return h ? new Date(h.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;
  };

  if (order.status === "cancelled") {
    return (
      <div className="border-2 border-[#DC2626] bg-red-50 p-6 flex items-center gap-4" data-testid="order-cancelled-banner">
        <XCircle className="w-8 h-8 text-[#DC2626] shrink-0" />
        <div>
          <div className="font-display font-black text-lg">Order Cancelled</div>
          <div className="text-sm text-gray-600">Questions? Call us at +1 (616) 410-0158</div>
        </div>
      </div>
    );
  }

  const currentIdx = Math.max(steps.findIndex((s) => s.key === order.status), 0);

  return (
    <div className="border border-gray-200 p-6 md:p-8" data-testid="order-status-timeline">
      <div className="flex items-center justify-between mb-8">
        <div className="ipv-eyebrow text-[#0A1C40]">Live Order Tracking</div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          Updates automatically
        </div>
      </div>

      <div className="relative">
        {steps.map((step, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          const ts = timeFor(step.key);
          const { Icon } = step;
          return (
            <div key={step.key} className="flex gap-4 relative" data-testid={`timeline-step-${step.key}`}>
              {i < steps.length - 1 && (
                <div
                  className={`absolute left-[19px] top-10 w-0.5 h-[calc(100%-24px)] ${
                    done ? "bg-[#0A1C40]" : "bg-gray-200"
                  }`}
                />
              )}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10 ${
                  done
                    ? "bg-[#0A1C40] text-white"
                    : current
                    ? "bg-[#0A1C40] text-white ring-4 ring-amber-100 animate-pulse"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {done ? <CheckCircle2 className="w-5 h-5" /> : current ? <Icon className="w-5 h-5" /> : <Clock className="w-4 h-4" />}
              </div>
              <div className={`pb-8 ${i === steps.length - 1 ? "pb-0" : ""}`}>
                <div className={`font-bold text-sm ${done || current ? "text-black" : "text-gray-400"}`}>
                  {step.label}
                  {current && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#0A1C40] text-white px-2 py-0.5">
                      Current
                    </span>
                  )}
                </div>
                <div className={`text-xs mt-0.5 ${done || current ? "text-gray-600" : "text-gray-400"}`}>
                  {step.desc}
                </div>
                {ts && <div className="text-[11px] text-[#0A1C40] font-semibold mt-1">{ts}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
